import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import axios from "axios";
import ConfirmInfoModal from "../components/ConfirmInfoModal";

const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 새로운 삭제 애니메이션 추가
const cardVariants = {
  initial: {
    opacity: 1,
    height: "auto",
    scale: 1,
    marginBottom: "12px",
    zIndex: 1,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  exit: {
    opacity: 0,
    height: 0,
    scale: 0.97,
    marginBottom: 0,
    zIndex: 0,
    boxShadow: "0 0 0 rgba(0,0,0,0)",
    overflow: "hidden",
    transition: {
      opacity: { duration: 0.35, ease: "easeOut" },
      scale: { duration: 0.4, ease: "easeInOut" },
      height: { duration: 0.45, ease: "easeInOut", delay: 0.1 },
      marginBottom: { duration: 0.45, ease: "easeOut", delay: 0.1 },
      boxShadow: { duration: 0.3, ease: "easeOut" },
      zIndex: { duration: 0.1 },
    },
  },
  deleting: {
    opacity: 0.8,
    scale: 0.99,
    boxShadow: "0 0 0 rgba(0,0,0,0)",
    backgroundColor: "#ffefef",
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
};

// 컨텐츠 애니메이션 변형 설정
const contentVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 버튼 애니메이션 변형 설정
const buttonVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.6,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// 아코디언 애니메이션
const accordionVariants = {
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

function CoOwnerInfoPage() {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [coOwners, setCoOwners] = useState([
    {
      id: 1,
      name: "",
      relation: "",
      birthDate: "",
      phone: "",
      isAccordionOpen: true,
      isCompleted: false,
      isEditing: true,
      originalData: null,
      nameError: false,
      nameInvalidCharError: false,
      phoneError: false,
      phoneSameError: false,
      birthDateError: false,
      isMinorError: false,
      showErrors: false,
      isDeleting: false, // 삭제 애니메이션 상태 추가
    },
  ]);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [relationDropdownOpen, setRelationDropdownOpen] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // 삭제 모달 상태 추가
  const [selectedCoOwnerId, setSelectedCoOwnerId] = useState(null); // 삭제할 공동소유자 ID
  const [isSending, setIsSending] = useState(false); // 전자서명 전송 중 상태
  const [sendingProgress, setSendingProgress] = useState(0); // 전송 진행 상태
  const [showSuccessModal, setShowSuccessModal] = useState(false); // 성공 모달 상태
  const [showConfirmModal, setShowConfirmModal] = useState(false); // 확인 모달 상태

  const relationOptions = [
    { value: "배우자", label: "배우자" },
    { value: "부모", label: "부모" },
    { value: "자녀", label: "자녀" },
    { value: "형제/자매", label: "형제/자매" },
    { value: "친척", label: "친척" },
    { value: "기타", label: "기타" },
  ];

  // 페이지가 렌더링 된 후 스크롤 테스트를 위한 useEffect 추가
  useEffect(() => {
    // DOM이 완전히 렌더링 될 때까지 충분한 시간을 줌
    setTimeout(() => {
      // 작성완료 버튼이 있는 하단으로 스크롤
      const completeButton = document.querySelector(".complete-button");
      if (completeButton) {
        // 작성완료 버튼으로 스크롤
        completeButton.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        // 버튼이 없으면 window 객체를 사용하여 페이지 하단으로 스크롤
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 500); // 1초 후 실행
  }, []);

  // useEffect(() => {
  //   // 쿠키에서 user_info 가져오기
  //   const userInfoFromCookie = Cookies.get(COOKIE_KEYS.USER_INFO);

  //   if (!userInfoFromCookie) {
  //     navigate("/not-found");
  //     return;
  //   }

  //   try {
  //     const parsedUserInfo = JSON.parse(userInfoFromCookie);
  //     setUserInfo(parsedUserInfo);

  //     // 쿠키 재설정 (만료 시간 갱신)
  //     Cookies.set(COOKIE_KEYS.USER_INFO, JSON.stringify(parsedUserInfo), {
  //       expires: COOKIE_EXPIRES.USER_INFO,
  //       ...COOKIE_OPTIONS,
  //     });
  //   } catch (error) {
  //     navigate("/not-found");
  //   }
  // }, [navigate]);

  // 모든 입력 필드가 유효한지 확인
  useEffect(() => {
    const updatedCoOwners = coOwners.map((owner) => {
      const isValid =
        owner.name.trim() !== "" &&
        owner.relation !== "" &&
        owner.birthDate.trim() !== "" &&
        owner.phone.trim() !== "";

      return {
        ...owner,
        isEditing: owner.isEditing,
      };
    });

    // 상태 업데이트가 필요한 경우에만 업데이트
    if (JSON.stringify(updatedCoOwners) !== JSON.stringify(coOwners)) {
      setCoOwners(updatedCoOwners);
    }

    const isAllCompleted = coOwners.every((owner) => owner.isCompleted);
    setIsNextEnabled(isAllCompleted);
  }, [coOwners]);

  const toggleAccordion = (id) => {
    // 현재 아코디언 상태 확인
    const isCurrentlyOpen = coOwners.find(
      (owner) => owner.id === id
    )?.isAccordionOpen;

    // 상태 변경 적용
    setCoOwners((prev) =>
      prev.map((owner) =>
        owner.id === id
          ? { ...owner, isAccordionOpen: !owner.isAccordionOpen }
          : owner
      )
    );

    // 아코디언이 열릴 때 (닫혀있던 것이 열리는 경우)
    if (!isCurrentlyOpen) {
      // 아코디언으로 스크롤만 처리 (스크롤 막지 않음)
      setTimeout(() => {
        const accordionElement = document.querySelector(
          `[data-accordion-id="${id}"]`
        );
        if (accordionElement) {
          // 아코디언 위치로 스크롤
          accordionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 50);
    }
  };

  const handleNameChange = (id, value) => {
    setCoOwners((prev) =>
      prev.map((owner) => {
        if (owner.id !== id) return owner;

        const newOwner = { ...owner, name: value };

        // 완료 상태였던 카드가 수정되는 경우 상태 업데이트
        if (
          owner.isCompleted &&
          owner.originalData &&
          (owner.originalData.name !== value ||
            owner.originalData.relation !== owner.relation ||
            owner.originalData.birthDate !== owner.birthDate ||
            owner.originalData.phone !== owner.phone)
        ) {
          newOwner.isCompleted = false;
        }

        return newOwner;
      })
    );

    // 이름이 입력된 후 다른 필드가 모두 유효한지 확인
    if (value.trim() !== "") {
      checkAllFieldsAndScroll(id);
    }
  };

  const handlePhoneChange = (id, value) => {
    // 전화번호는 숫자만 입력 가능
    if (!/^\d*$/.test(value)) return;

    // 11자리로 제한
    if (value.length > 11) return;

    // 전화번호 정규식 검증 (010으로 시작하고 총 11자리)
    const isValidPhone = /^010\d{8}$/.test(value);

    setCoOwners((prev) =>
      prev.map((owner) => {
        if (owner.id !== id) return owner;

        const newOwner = {
          ...owner,
          phone: value,
          // 값이 있고 길이가 11자리면 정규식 검증, 그렇지 않으면 에러 없음
          phoneError: value.length === 11 && !isValidPhone,
          // 번호를 수정하면 신청인 중복 에러는 초기화
          phoneSameError: false,
        };

        // 완료 상태였던 카드가 수정되는 경우 상태 업데이트
        if (
          owner.isCompleted &&
          owner.originalData &&
          (owner.originalData.name !== owner.name ||
            owner.originalData.relation !== owner.relation ||
            owner.originalData.birthDate !== owner.birthDate ||
            owner.originalData.phone !== value)
        ) {
          newOwner.isCompleted = false;
        }

        return newOwner;
      })
    );

    // 올바르지 않은 형식일 때 진동 효과
    if (value.length === 11 && !/^010\d{8}$/.test(value)) {
      const input = document.querySelector(
        `input[type="tel"][value="${value}"]`
      );
      if (input) {
        input.style.animation = "shake-mild 0.3s";
        setTimeout(() => {
          input.style.animation = "";
        }, 300);
      }
    }

    // 올바른 형식일 때 모든 필수 정보가 입력되었는지 확인
    if (value.length === 11 && /^010\d{8}$/.test(value)) {
      checkAllFieldsAndScroll(id);

      // 연락처가 11자리가 되면 키보드 닫기
      setTimeout(() => {
        const phoneInput = document.querySelector(
          `input[data-owner-id="${id}"][data-field-type="phone"]`
        );
        if (phoneInput) {
          phoneInput.blur();
        }
      }, 100);
    }
  };

  const handleRelationClick = (id) => {
    // 다른 관계 드롭다운이 열려있으면 닫기
    if (relationDropdownOpen !== null && relationDropdownOpen !== id) {
      setRelationDropdownOpen(null);

      // 이전에 열린 드롭다운 닫을 때 스크롤 복원
      document.body.classList.remove("dropdown-open");

      document.body.style.overflow = "auto";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.height = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.touchAction = "";
      const scrollY = parseInt(document.body.dataset.scrollY || "0");
      window.scrollTo(0, scrollY);
    }

    // 드롭다운 토글
    const isOpening = relationDropdownOpen !== id;
    setRelationDropdownOpen(isOpening ? id : null);

    // 드롭다운 상태에 따라 제어
    if (isOpening) {
      // 스크롤 위치 저장
      document.body.dataset.scrollY = window.scrollY;

      // 기존 스크롤 방지 유지
      document.body.classList.add("dropdown-open");
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.overscrollBehavior = "none";
      document.body.style.touchAction = "none";
    } else {
      // 드롭다운 닫힐 때 스크롤 위치 복원
      document.body.classList.remove("dropdown-open");
      document.body.style.overflow = "auto";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.body.style.top = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.touchAction = "";
      const scrollY = parseInt(document.body.dataset.scrollY || "0");
      window.scrollTo(0, scrollY);
    }
  };

  const handleResidentNumberChange = (id, value) => {
    // 생년월일은 숫자만 입력 가능
    if (!/^\d*$/.test(value)) return;

    // 생년월일은 8자리로 제한 (YYYYMMDD)
    if (value.length > 8) return;

    // 생년월일 정규식 검증 (YYYYMMDD 형식)
    const isValidBirthDate = /^\d{8}$/.test(value) && isValidDate(value);

    setCoOwners((prev) =>
      prev.map((owner) => {
        if (owner.id !== id) return owner;

        const newOwner = {
          ...owner,
          birthDate: value,
          // 값이 있고 길이가 8자리면 정규식 검증, 그렇지 않으면 에러 없음
          birthDateError: value.length === 8 && !isValidBirthDate,
        };

        // 완료 상태였던 카드가 수정되는 경우 상태 업데이트
        if (
          owner.isCompleted &&
          owner.originalData &&
          (owner.originalData.name !== owner.name ||
            owner.originalData.relation !== owner.relation ||
            owner.originalData.birthDate !== value ||
            owner.originalData.phone !== owner.phone)
        ) {
          newOwner.isCompleted = false;
        }

        return newOwner;
      })
    );

    // 올바르지 않은 형식일 때 진동 효과
    if (value.length === 8 && !isValidDate(value)) {
      setTimeout(() => {
        const cards = document.querySelectorAll(".co-owner-card");
        // 모든 카드를 순회하면서 필드 찾기
        cards.forEach((card) => {
          const inputs = card.querySelectorAll("input");
          for (let input of inputs) {
            // value와 placeholder로 생년월일 필드 찾기
            if (
              (input.value === value &&
                input.placeholder &&
                input.placeholder.includes("생년월일")) ||
              input.value === value
            ) {
              // 애니메이션 리셋 후 적용
              input.style.animation = "none";
              // eslint-disable-next-line no-unused-expressions
              input.offsetHeight; // Trigger reflow
              input.style.animation = "shake-mild 0.3s";

              setTimeout(() => {
                input.style.animation = "";
              }, 300);
              return; // 필드를 찾았으므로 반복 중단
            }
          }
        });
      }, 10);
    }

    // 올바른 형식일 때 모든 필수 정보가 입력되었는지 확인
    if (value.length === 8 && isValidDate(value)) {
      checkAllFieldsAndScroll(id);

      // 생년월일이 8자리가 되면 키보드 닫기
      setTimeout(() => {
        const birthDateInput = document.querySelector(
          `input[data-owner-id="${id}"][data-field-type="birthDate"]`
        );
        if (birthDateInput) {
          birthDateInput.blur();
        }
      }, 100);
    }
  };

  // 유효한 날짜인지 확인하는 함수
  const isValidDate = (dateString) => {
    // YYYYMMDD 형식 체크
    if (!/^\d{8}$/.test(dateString)) return false;

    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6));
    const day = parseInt(dateString.substring(6, 8));

    // 유효한 년도 범위 (1900~현재)
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) return false;

    // 유효한 월 체크 (1-12)
    if (month < 1 || month > 12) return false;

    // 유효한 일 체크
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > lastDayOfMonth) return false;

    return true;
  };

  // 모든 필수 필드가 입력되었는지 확인하고 스크롤 처리하는 함수
  const checkAllFieldsAndScroll = (id) => {
    // 현재 편집 중인 공동소유자 정보 가져오기
    const owner = coOwners.find((o) => o.id === id);
    if (!owner) return;

    // 이름, 연락처, 생년월일이 모두 유효한지 확인
    const isNameValid = owner.name.trim() !== "";
    const isPhoneValid = /^010\d{8}$/.test(owner.phone);
    const isBirthDateValid =
      /^\d{8}$/.test(owner.birthDate) && isValidDate(owner.birthDate);

    // 모든 필드가 유효하면 스크롤 처리
    if (isNameValid && isPhoneValid && isBirthDateValid) {
      // 약간의 지연 후 스크롤 처리 (상태 업데이트 후)
      setTimeout(() => {
        // 작성완료 버튼 찾기
        const completeButton = document.querySelector(
          `[data-accordion-id="${id}"] .complete-button`
        );
        if (completeButton) {
          // 작성완료 버튼으로 전체 페이지 스크롤
          completeButton.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else {
          // 다른 방법으로 스크롤 시도 - 현재 카드의 하단으로
          const currentCard = document.querySelector(
            `[data-accordion-id="${id}"]`
          );
          if (currentCard) {
            // 카드의 하단으로 스크롤
            window.scrollTo({
              top: currentCard.offsetTop + currentCard.offsetHeight - 100,
              behavior: "smooth",
            });
          }
        }
      }, 100);
    }
  };

  const handleRelationSelect = (id, value) => {
    setCoOwners((prev) =>
      prev.map((owner) => {
        if (owner.id !== id) return owner;

        const newOwner = { ...owner, relation: value };

        // 완료 상태였던 카드가 수정되는 경우 상태 업데이트
        if (
          owner.isCompleted &&
          owner.originalData &&
          (owner.originalData.name !== owner.name ||
            owner.originalData.relation !== value ||
            owner.originalData.birthDate !== owner.birthDate ||
            owner.originalData.phone !== owner.phone)
        ) {
          newOwner.isCompleted = false;
        }

        return newOwner;
      })
    );
    setRelationDropdownOpen(null);

    // 드롭다운 닫힐 때 스크롤 위치 복원
    document.body.classList.remove("dropdown-open");
    document.body.style.overflow = "auto";
    document.body.style.position = "";
    document.body.style.width = "";
    document.body.style.height = "";
    document.body.style.top = "";
    document.body.style.overscrollBehavior = "";
    document.body.style.touchAction = "";
    const scrollY = parseInt(document.body.dataset.scrollY || "0");
    window.scrollTo(0, scrollY);
  };

  const addCoOwner = () => {
    // 최대 3명까지만 추가 가능
    if (coOwners.length >= 3) {
      alert("공동소유자는 최대 3명까지만 추가할 수 있습니다.");
      return;
    }

    const newId =
      coOwners.length > 0
        ? Math.max(...coOwners.map((owner) => owner.id)) + 1
        : 1;

    setCoOwners((prev) => [
      ...prev,
      {
        id: newId,
        name: "",
        relation: "",
        birthDate: "",
        phone: "",
        isAccordionOpen: true,
        isCompleted: false,
        isEditing: true,
        originalData: null,
        nameError: false,
        nameInvalidCharError: false,
        phoneError: false,
        phoneSameError: false,
        birthDateError: false,
        isMinorError: false,
        showErrors: false,
        isDeleting: false, // 삭제 애니메이션 상태 추가
      },
    ]);

    // 새 공동소유자 추가 후 스크롤을 조정
    setTimeout(() => {
      // 뷰포트 높이 저장
      const viewportHeight = window.innerHeight;

      // 하단 버튼 영역 높이 (대략 150px)
      const bottomBarHeight = 150;

      // 새로 추가된 카드 요소 찾기
      const newCardQuery = `[data-accordion-id="${newId}"]`;

      // 약간의 지연 후 다시 스크롤 실행 (DOM 업데이트 후)
      setTimeout(() => {
        const newCard = document.querySelector(newCardQuery);
        if (newCard) {
          // 카드의 위치 계산
          const cardRect = newCard.getBoundingClientRect();
          const cardTop = cardRect.top;
          const cardHeight = cardRect.height;

          // 카드가 화면 밖에 있거나 하단 버튼에 가려지는 경우
          if (cardTop + cardHeight > viewportHeight - bottomBarHeight) {
            // 카드가 화면에 보이도록 스크롤 조정
            newCard.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        } else {
          // 카드를 찾을 수 없는 경우 가장 마지막 카드로 스크롤
          const allCards = document.querySelectorAll(".co-owner-card");
          if (allCards.length > 0) {
            const lastCard = allCards[allCards.length - 1];
            lastCard.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 100);
    }, 50);
  };

  // 데이터가 입력되었는지 확인하는 함수
  const hasInputData = (owner) => {
    return (
      owner.name.trim() !== "" ||
      owner.relation !== "" ||
      owner.birthDate.trim() !== "" ||
      owner.phone.trim() !== ""
    );
  };

  // 삭제 버튼 클릭 핸들러 수정
  const handleDeleteCoOwnerClick = (id, e) => {
    e.stopPropagation();

    // 삭제할 공동소유자 찾기
    const owner = coOwners.find((o) => o.id === id);
    if (!owner) return;

    // 최소 1개의 공동소유자는 유지
    if (coOwners.length <= 1) {
      return;
    }

    // 항상 확인 모달 표시
    setSelectedCoOwnerId(id);
    setShowDeleteModal(true);
  };

  // 실제 삭제 처리 함수
  const deleteCoOwner = (id) => {
    // 먼저 isDeleting 플래그를 설정하여 애니메이션 시작
    setCoOwners((prev) =>
      prev.map((owner) =>
        owner.id === id ? { ...owner, isDeleting: true } : owner
      )
    );

    // 애니메이션 완료 후 실제 삭제
    setTimeout(() => {
      setCoOwners((prev) => prev.filter((owner) => owner.id !== id));
    }, 550); // 애니메이션 지속 시간보다 약간 더 길게 설정 (450ms → 550ms)

    // 모달 닫기
    setShowDeleteModal(false);
    setSelectedCoOwnerId(null);
  };

  // 모달 닫기 함수
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedCoOwnerId(null);
  };

  const handleCompleteCoOwner = (id) => {
    // 해당 카드 찾기
    const owner = coOwners.find((o) => o.id === id);
    if (!owner) return;

    // 미성년자 검증 함수 (2007년 1월 1일 이후 출생자는 미성년자)
    const isMinor = (birthDate) => {
      if (!/^\d{8}$/.test(birthDate)) return false;

      const year = parseInt(birthDate.substring(0, 4));
      const month = parseInt(birthDate.substring(4, 6));
      const day = parseInt(birthDate.substring(6, 8));

      const birthDateObj = new Date(year, month - 1, day);
      const cutoffDate = new Date(2007, 0, 1); // 2007년 1월 1일

      return birthDateObj >= cutoffDate;
    };

    // 이름 검증 (자음/모음만 있는 경우 - 모바일 호환성 개선)
    const hasIncompleteHangul = /^[ㄱ-ㅎㅏ-ㅣ]+$/.test(owner.name.trim());
    // 한글이 아닌 문자 검증 (한글만 허용)
    const hasNonKoreanCharacters = /[^가-힣\s]/.test(owner.name.trim());
    const isNameValid =
      owner.name.trim() !== "" &&
      !hasIncompleteHangul &&
      !hasNonKoreanCharacters;

    // 전화번호 검증
    const isPhoneValid = /^010\d{8}$/.test(owner.phone);
    const isPhoneNotEmpty = owner.phone.trim() !== "";

    // 신청인(신청자) 연락처와 동일하면 등록 불가
    const getApplicantPhone = () => {
      try {
        const cookieData = Cookies.get("buildingUnitInfo");
        if (cookieData) {
          const parsed = JSON.parse(cookieData);
          return (parsed.applicantPhone || "").replace(/[^\d]/g, "");
        }
      } catch (e) {}
      return "";
    };
    const applicantPhone = getApplicantPhone();
    const isSameAsApplicant =
      isPhoneValid &&
      applicantPhone !== "" &&
      owner.phone.replace(/[^\d]/g, "") === applicantPhone;

    // 생년월일 검증
    const isBirthDateValid =
      /^\d{8}$/.test(owner.birthDate) && isValidDate(owner.birthDate);
    const isBirthDateNotEmpty = owner.birthDate.trim() !== "";
    const isNotMinor = !isMinor(owner.birthDate);

    const isValid =
      isNameValid &&
      isBirthDateNotEmpty &&
      isBirthDateValid &&
      isNotMinor &&
      isPhoneNotEmpty &&
      isPhoneValid &&
      !isSameAsApplicant;

    // 유효성 검사 실패 시 에러 표시
    if (!isValid) {
      setCoOwners((prev) => {
        return prev.map((o) =>
          o.id === id
            ? {
                ...o,
                showErrors: true,
                nameError: !isNameValid && !hasNonKoreanCharacters,
                nameInvalidCharError: hasNonKoreanCharacters,
                phoneError: !isPhoneNotEmpty || !isPhoneValid,
                phoneSameError: isSameAsApplicant, // 신청인과 연락처 동일 에러
                birthDateError: !isBirthDateNotEmpty || !isBirthDateValid,
                isMinorError: !isNotMinor && isBirthDateValid, // 미성년자 에러 추가
              }
            : o
        );
      });
      return;
    }

    // 유효성 검사 통과 시 완료 처리
    setCoOwners((prev) => {
      const updatedCoOwners = prev.map((o) =>
        o.id === id
          ? {
              ...o,
              isAccordionOpen: false,
              isCompleted: true,
              isEditing: false,
              showErrors: false,
              nameError: false,
              nameInvalidCharError: false,
              phoneError: false,
              phoneSameError: false,
              birthDateError: false,
              isMinorError: false,
              originalData: {
                name: o.name,
                relation: o.relation,
                birthDate: o.birthDate,
                phone: o.phone,
              },
            }
          : o
      );

      // 모든 공동소유자가 완료되었는지 확인하여 다음 버튼 활성화
      const allCompleted = updatedCoOwners.every((o) => o.isCompleted);
      setIsNextEnabled(allCompleted);

      return updatedCoOwners;
    });
  };

  // 수정 모드 활성화 함수
  const handleEditCoOwner = (id) => {
    setCoOwners((prev) =>
      prev.map((owner) =>
        owner.id === id
          ? {
              ...owner,
              isAccordionOpen: true,
              isEditing: true,
            }
          : owner
      )
    );
  };

  // 입력 데이터 변경 시 수정 가능 상태 확인
  const canEditCoOwner = (owner) => {
    if (!owner.isCompleted) return false;

    // 원본 데이터와 비교하기 위한 로직 (이 예제에서는 항상 true 반환)
    return true;
  };

  // 데이터가 입력되기 시작했는지 확인하는 함수
  const hasStartedInput = (owner) => {
    return (
      owner.name.trim() !== "" ||
      owner.relation !== "" ||
      owner.birthDate.trim() !== "" ||
      owner.phone.trim() !== ""
    );
  };

  const handleNext = async () => {
    // 확인 모달 표시
    setShowConfirmModal(true);
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmModalConfirm = async () => {
    setShowConfirmModal(false);

    // 완료된 공동소유자만 필터링
    const completedCoOwners = coOwners.filter((owner) => owner.isCompleted);

    // buildingUnitInfo 쿠키에 공동소유자 정보 저장
    const currentBuildingUnitInfo = Cookies.get("buildingUnitInfo");
    if (currentBuildingUnitInfo) {
      try {
        const parsedBuildingUnitInfo = JSON.parse(currentBuildingUnitInfo);

        const updatedBuildingUnitInfo = {
          ...parsedBuildingUnitInfo,
          coOwners: completedCoOwners.map((owner) => ({
            name: owner.name,
            relation: owner.relation,
            birthDate: owner.birthDate,
            phone: owner.phone,
          })),
        };

        Cookies.set(
          "buildingUnitInfo",
          JSON.stringify(updatedBuildingUnitInfo)
        );
      } catch (error) {
        console.error("쿠키 파싱 오류:", error);
      }
    }

    // 다음 페이지로 이동
    navigate("/bond-agreement");
  };

  // 가로 스크롤 방지 및 세로 스크롤만 활성화
  useEffect(() => {
    // 기존 스타일 저장
    const originalStyle = document.body.style.cssText;

    // 스크롤바 숨기기 및 스크롤 설정
    document.body.style.scrollbarWidth = "none"; // Firefox
    document.body.style.msOverflowStyle = "none"; // IE, Edge
    document.body.style.overflow = "auto";
    document.body.style.overflowX = "hidden";
    document.documentElement.style.setProperty("--scrollbar-width", "0px");

    // 컴포넌트 언마운트 시 원래 스타일로 복원
    return () => {
      document.body.style.cssText = originalStyle;
    };
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (relationDropdownOpen !== null) {
        const dropdown = document.querySelector(".relation-dropdown");
        const trigger = document.querySelector(
          `[data-id="${relationDropdownOpen}"]`
        );

        if (
          dropdown &&
          trigger &&
          !dropdown.contains(event.target) &&
          !trigger.contains(event.target)
        ) {
          setRelationDropdownOpen(null);

          // 스크롤 복원
          document.body.style.overflow = "auto";
          document.body.style.position = "";
          document.body.style.width = "";
          document.body.style.top = "";

          // 스크롤 위치 복원
          const scrollY = parseInt(document.body.dataset.scrollY || "0");
          window.scrollTo(0, scrollY);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [relationDropdownOpen]);

  // 컴포넌트 언마운트 시 body 스타일 복원
  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.touchAction = "";
      document.body.classList.remove("dropdown-open");
    };
  }, []);

  // 데이터가 변경되었는지 확인하는 함수
  const isDataChanged = (owner) => {
    if (!owner.originalData) return false;

    return (
      owner.name !== owner.originalData.name ||
      owner.relation !== owner.originalData.relation ||
      owner.birthDate !== owner.originalData.birthDate ||
      owner.phone !== owner.originalData.phone
    );
  };

  // 모달용 공동소유자 정보 준비
  const getCoOwnerInfoForModal = () => {
    const completedCoOwners = coOwners.filter((owner) => owner.isCompleted);
    return {
      totalCount: completedCoOwners.length,
      coOwners: completedCoOwners.map((owner, index) => ({
        id: index + 1,
        name: owner.name,
        phone: owner.phone,
        birthDate: owner.birthDate,
        relation: owner.relation,
      })),
    };
  };

  // 입력이 유효한지 확인하는 함수 (에러 표시용)
  const checkInputErrors = (owner) => {
    // 빈 입력 체크 (nameError가 false일 때만 빈 값 에러 표시)
    const nameError =
      owner.showErrors && owner.name.trim() === "" && !owner.nameError;
    const relationError = owner.showErrors && owner.relation === "";
    const birthDateInputError =
      owner.showErrors &&
      owner.birthDate.trim() === "" &&
      !owner.birthDateError;
    const phoneInputError =
      owner.showErrors && owner.phone.trim() === "" && !owner.phoneError;

    return {
      nameError,
      relationError,
      birthDateInputError,
      phoneInputError,
    };
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="page-container"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          padding: "0 24px",
          position: "relative",
          paddingBottom: "104px",
        }}
      >
        <div
          style={{
            marginTop: "20px",
          }}
          className="page-header"
        >
          <h1>공동소유자 확인</h1>
        </div>

        <motion.div
          className="co-owner-info-content"
          variants={contentVariants}
          initial="initial"
          animate="animate"
          style={{
            width: "100%",
            maxWidth: "460px",
            paddingBottom: "100px",
            overflowY: "auto",
            height: "100%",
            marginTop: "20px",
          }}
        >
          <div className="info-section">
            <h2
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#2E3840",
                marginBottom: "16px",
              }}
            >
              공동소유자 정보입력
            </h2>
            <p
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#4B5563",
                marginBottom: "24px",
                lineHeight: "1.6",
              }}
            >
              이 부동산에 귀하 외에 공동소유자가 있는 경우
              <br />
              정보를 입력해주세요.
            </p>

            <div
              style={{
                marginTop: "30px",
                width: "100%",
              }}
              className="co-owners-container"
            >
              <AnimatePresence>
                {coOwners.map((owner, index) => (
                  <motion.div
                    key={owner.id}
                    className={`co-owner-card ${
                      owner.isAccordionOpen ? "accordion-open" : ""
                    } ${
                      relationDropdownOpen === owner.id ? "card-no-scroll" : ""
                    }`}
                    data-accordion-id={owner.id}
                    variants={cardVariants}
                    initial="initial"
                    animate={
                      owner.isDeleting ? ["deleting", "exit"] : "initial"
                    }
                    exit="exit"
                    style={{
                      marginBottom: owner.isAccordionOpen ? "16px" : "12px",
                      border: "1px solid #F3F4F6",
                      borderRadius: "8px",
                      overflow:
                        relationDropdownOpen === owner.id
                          ? "hidden"
                          : "visible",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      position:
                        relationDropdownOpen === owner.id
                          ? "relative"
                          : "static",
                      zIndex: relationDropdownOpen === owner.id ? 9900 : "auto",
                      transformOrigin: "center center",
                    }}
                  >
                    <div
                      className="card-header"
                      onClick={() => toggleAccordion(owner.id)}
                      style={{
                        padding: "16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: owner.isCompleted
                          ? "#F0FDF4"
                          : hasStartedInput(owner)
                          ? "#FAFAFF"
                          : "#F9FAFB",
                        cursor: "pointer",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        borderBottomRightRadius: "8px",
                        borderBottomLeftRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <img
                          src={
                            owner.isCompleted
                              ? "/icon-park-outline_peoplesuc.png"
                              : "/icon-park-outline_people.png"
                          }
                          alt="소유자 아이콘"
                          style={{
                            width: "20px",
                            height: "20px",
                          }}
                        />
                        <span style={{ fontWeight: "600" }}>
                          {owner.name ? owner.name : `공동소유자${index + 1}`}
                        </span>
                        {hasStartedInput(owner) && !owner.isCompleted && (
                          <span
                            style={{
                              color: "#7A4FFF",
                              fontSize: "14px",
                              marginLeft: "4px",
                            }}
                          >
                            입력중
                          </span>
                        )}
                        {owner.isCompleted && (
                          <span
                            style={{
                              color: "#16A34A",
                              fontSize: "14px",
                              marginLeft: "4px",
                            }}
                          >
                            완료
                          </span>
                        )}
                      </div>
                      <img
                        src={
                          owner.isAccordionOpen
                            ? "/mingcute_down-line.png"
                            : "/mingcute_down-line.png"
                        }
                        alt="펼치기/접기"
                        style={{
                          width: "20px",
                          height: "20px",
                          transition: "transform 0.3s ease",
                          transform: owner.isAccordionOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </div>

                    <AnimatePresence>
                      {owner.isAccordionOpen && (
                        <motion.div
                          initial="closed"
                          animate="open"
                          exit="closed"
                          variants={accordionVariants}
                          style={{
                            overflow: "visible",
                          }}
                        >
                          <div
                            className="card-body"
                            style={{
                              padding: "16px",
                            }}
                          >
                            <div
                              className="form-group"
                              style={{ marginBottom: "16px" }}
                            >
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "8px",
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  color: "#4B5563",
                                }}
                              >
                                이름<span style={{ color: "#FF4D4F" }}>*</span>
                              </label>
                              <input
                                type="text"
                                lang="ko"
                                value={owner.name}
                                onChange={(e) =>
                                  handleNameChange(owner.id, e.target.value)
                                }
                                placeholder="이름을 입력해주세요"
                                style={{
                                  width: "100%",
                                  padding: "12px",
                                  border: `2px solid ${
                                    owner.nameError ||
                                    owner.nameInvalidCharError
                                      ? "#DC4B3F"
                                      : "#F3F4F6"
                                  }`,
                                  borderRadius: "8px",
                                  fontSize: "16px",
                                  outline: "none",
                                }}
                                onFocus={(e) => {
                                  if (
                                    !owner.nameError &&
                                    !owner.nameInvalidCharError
                                  ) {
                                    e.target.style.borderColor = "#7A4FFF";
                                    e.target.style.outline = "none";
                                  }
                                }}
                                onBlur={(e) => {
                                  if (
                                    !owner.nameError &&
                                    !owner.nameInvalidCharError
                                  ) {
                                    e.target.style.borderColor = "#F3F4F6";
                                    e.target.style.outline = "none";
                                  }
                                }}
                              />
                              {owner.nameInvalidCharError && (
                                <div className="error-message">
                                  이름은 한글로만 입력해주세요.
                                </div>
                              )}
                              {owner.nameError &&
                                !owner.nameInvalidCharError && (
                                  <div className="error-message">
                                    이름을 정확히 입력해주세요.
                                  </div>
                                )}
                              {!owner.nameError &&
                                !owner.nameInvalidCharError &&
                                checkInputErrors(owner).nameError && (
                                  <div className="error-message">
                                    이름을 입력해주세요.
                                  </div>
                                )}
                            </div>

                            <div
                              className="form-group"
                              style={{ marginBottom: "16px" }}
                            >
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "8px",
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  color: "#4B5563",
                                }}
                              >
                                연락처
                                <span style={{ color: "#FF4D4F" }}>*</span>
                              </label>
                              <input
                                type="tel"
                                data-owner-id={owner.id}
                                data-field-type="phone"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={owner.phone}
                                onChange={(e) =>
                                  handlePhoneChange(owner.id, e.target.value)
                                }
                                placeholder="핸드폰 번호를 입력해주세요 (- 없이)"
                                style={{
                                  width: "100%",
                                  padding: "12px",
                                  border: `2px solid ${
                                    owner.phoneError ||
                                    owner.phoneSameError ||
                                    checkInputErrors(owner).phoneInputError
                                      ? "#DC4B3F"
                                      : "#F3F4F6"
                                  }`,
                                  borderRadius: "8px",
                                  fontSize: "16px",
                                  outline: "none",
                                }}
                                maxLength={11}
                                onFocus={(e) => {
                                  if (
                                    !owner.phoneError &&
                                    !owner.phoneSameError &&
                                    !checkInputErrors(owner).phoneInputError
                                  ) {
                                    e.target.style.borderColor = "#7A4FFF";
                                    e.target.style.outline = "none";
                                  }
                                }}
                                onBlur={(e) => {
                                  if (
                                    !owner.phoneError &&
                                    !owner.phoneSameError &&
                                    !checkInputErrors(owner).phoneInputError
                                  ) {
                                    e.target.style.borderColor = "#F3F4F6";
                                    e.target.style.outline = "none";
                                  }
                                }}
                              />
                              {owner.phoneError && (
                                <div className="error-message">
                                  올바른 전화번호를 입력해주세요.
                                  <br />
                                  (010으로 시작하는 11자리)
                                </div>
                              )}
                              {!owner.phoneError && owner.phoneSameError && (
                                <div className="error-message">
                                  신청인과 연락처가 같습니다.
                                </div>
                              )}
                              {!owner.phoneError &&
                                !owner.phoneSameError &&
                                checkInputErrors(owner).phoneInputError && (
                                  <div className="error-message">
                                    휴대폰 번호를 입력해주세요.
                                  </div>
                                )}
                            </div>

                            <div
                              className="form-group"
                              style={{
                                marginBottom: "16px",
                                position: "relative",
                                marginTop: "16px",
                              }}
                            >
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "8px",
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  color: "#4B5563",
                                }}
                              >
                                생년월일
                                <span style={{ color: "#FF4D4F" }}>*</span>
                              </label>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <input
                                  type="tel"
                                  data-owner-id={owner.id}
                                  data-field-type="birthDate"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={owner.birthDate}
                                  onChange={(e) =>
                                    handleResidentNumberChange(
                                      owner.id,
                                      e.target.value
                                    )
                                  }
                                  placeholder="생년월일 8자리 (YYYYMMDD)"
                                  style={{
                                    width: "100%",
                                    padding: "12px",
                                    border: `2px solid ${
                                      owner.birthDateError ||
                                      checkInputErrors(owner)
                                        .birthDateInputError
                                        ? "#DC4B3F"
                                        : "#F3F4F6"
                                    }`,
                                    borderRadius: "8px",
                                    fontSize: "16px",
                                    outline: "none",
                                  }}
                                  maxLength={8}
                                  onFocus={(e) => {
                                    if (
                                      !owner.birthDateError &&
                                      !checkInputErrors(owner)
                                        .birthDateInputError
                                    ) {
                                      e.target.style.borderColor = "#7A4FFF";
                                      e.target.style.outline = "none";
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (
                                      !owner.birthDateError &&
                                      !checkInputErrors(owner)
                                        .birthDateInputError
                                    ) {
                                      e.target.style.borderColor = "#F3F4F6";
                                      e.target.style.outline = "none";
                                    }
                                  }}
                                />
                              </div>
                              {owner.isMinorError && (
                                <div className="error-message">
                                  미성년자는 오프라인신청대상입니다.
                                </div>
                              )}
                              {owner.birthDateError && !owner.isMinorError && (
                                <div className="error-message">
                                  올바른 생년월일을 입력해주세요. (YYYYMMDD)
                                </div>
                              )}
                              {!owner.birthDateError &&
                                !owner.isMinorError &&
                                checkInputErrors(owner).birthDateInputError && (
                                  <div className="error-message">
                                    생년월일을 입력해주세요.
                                  </div>
                                )}
                            </div>

                            {/* <div
                              className="form-group"
                              style={{
                                marginBottom: "16px",
                                position: "relative",
                                marginTop: "16px",
                              }}
                            >
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "8px",
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  color: "#4B5563",
                                }}
                              >
                                관계<span style={{ color: "#FF4D4F" }}>*</span>
                              </label>
                              <div
                                data-id={owner.id}
                                className="relation-selector"
                                onClick={(e) => handleRelationClick(owner.id)}
                                style={{
                                  width: "100%",
                                  padding: "12px",
                                  border: `1px solid ${
                                    checkInputErrors(owner).relationError
                                      ? "#DC4B3F"
                                      : "#E5E7EB"
                                  }`,
                                  borderRadius: "8px",
                                  fontSize: "16px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  backgroundColor: "white",
                                }}
                              >
                                <span
                                  style={{
                                    color: owner.relation ? "#000" : "#9CA3AF",
                                  }}
                                >
                                  {owner.relation || "관계를 선택하세요"}
                                </span>
                                <img
                                  src="/Dropdownicon.svg"
                                  alt="선택"
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    transform:
                                      relationDropdownOpen === owner.id
                                        ? "rotate(180deg)"
                                        : "rotate(0)",
                                    transition: "transform 0.3s ease",
                                  }}
                                />
                              </div>
                              {checkInputErrors(owner).relationError && (
                                <div className="error-message">
                                  관계를 선택해주세요.
                                </div>
                              )}

                              {relationDropdownOpen === owner.id && (
                                <>
                                  <div
                                    className="relation-dropdown-backdrop"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRelationClick(owner.id);
                                    }}
                                  />
                                  <div
                                    className="relation-dropdown"
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      backgroundColor: "white",
                                      border: "1px solid #F3F4F6",
                                      borderRadius: "8px",
                                      marginTop: "4px",
                                      zIndex: 99999,
                                      boxShadow:
                                        "0 4px 12px rgba(0, 0, 0, 0.15)",
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    {relationOptions.map((option) => (
                                      <div
                                        key={option.value}
                                        onClick={(e) =>
                                          handleRelationSelect(
                                            owner.id,
                                            option.value
                                          )
                                        }
                                        style={{
                                          padding: "12px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #F3F4F6",
                                          fontSize: "16px",
                                          backgroundColor: "white",
                                          transition:
                                            "background-color 0.2s ease",
                                        }}
                                        onMouseOver={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            "#F9FAFB")
                                        }
                                        onMouseOut={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            "white")
                                        }
                                      >
                                        {option.label}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div> */}

                            {/* 하단 버튼 영역 */}
                            <div
                              style={{
                                marginTop: "24px",
                                marginBottom: "10px",
                                display: "flex",
                                flexDirection: "row",
                                gap: "12px",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCoOwnerClick(owner.id, e);
                                }}
                                style={{
                                  // flex: "1",
                                  width: "20%",
                                  height: "48px",
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  background: "white",
                                  color: "#DC4B3F",
                                  // border: "1px solid #DC4B3F",
                                  // borderRadius: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "start",
                                  gap: "4px",
                                }}
                              >
                                <img
                                  src="/material-symbols_delete-outline.png"
                                  alt="삭제"
                                  style={{ width: "18px", height: "18px" }}
                                />
                                삭제
                              </div>

                              {owner.isCompleted ? (
                                // 완료 상태일 때는 수정 버튼 표시
                                <button
                                  // className="edit-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCoOwner(owner.id);
                                  }}
                                  disabled={!isDataChanged(owner)}
                                  style={{
                                    // flex: "1",
                                    height: "40px",
                                    width: "35%",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    background: isDataChanged(owner)
                                      ? "#7A4FFF"
                                      : "#E1DCFF",
                                    color: isDataChanged(owner)
                                      ? "white"
                                      : "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: isDataChanged(owner)
                                      ? "pointer"
                                      : "not-allowed",
                                    transition: "background-color 0.2s ease",
                                  }}
                                >
                                  수정하기
                                </button>
                              ) : (
                                // 미완료 상태일 때는 작성완료 버튼 표시 (항상 활성화)
                                <button
                                  className="complete-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCompleteCoOwner(owner.id);
                                  }}
                                  style={{
                                    // flex: "1",
                                    width: "35%",
                                    height: "40px",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    background: "#7A4FFF",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s ease",
                                    boxShadow:
                                      "0 2px 5px rgba(0, 117, 255, 0.2)",
                                  }}
                                >
                                  작성완료
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* 삭제 확인 모달 */}
        {showDeleteModal && (
          <div
            className="delete-modal-backdrop"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
            onClick={closeDeleteModal}
          >
            <div
              className="delete-modal"
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "24px",
                width: "320px",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                textAlign: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 경고 아이콘 */}
              <div style={{ marginBottom: "20px" }}>
                <img
                  src="/warning.png"
                  alt="경고"
                  style={{
                    width: "75px",
                    height: "75px",
                  }}
                />
              </div>

              {/* 제목 */}
              <div style={{ marginBottom: "16px" }}>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    margin: 0,
                    color: "#000000",
                  }}
                >
                  공동소유자 삭제
                </h3>
              </div>

              {/* 메시지 */}
              <div style={{ marginBottom: "24px" }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    lineHeight: "1.5",
                    color: "#000000",
                  }}
                >
                  정말 이 공동소유자를 삭제하시겠습니까?
                  <br />이 작업은 되돌릴 수 없습니다.
                </p>
              </div>

              {/* 버튼 */}
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="delete-modal-button cancel"
                  onClick={closeDeleteModal}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    backgroundColor: "white",
                    color: "#777777",
                    border: "1px solid #DDDDDD",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  취소하기
                </button>
                <button
                  className="delete-modal-button delete"
                  onClick={() => deleteCoOwner(selectedCoOwnerId)}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    backgroundColor: "#DC4B3F",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  삭제하기
                </button>
              </div>
            </div>
          </div>
        )}

        <motion.div
          style={{
            position: "fixed",
            bottom: -45,
            left: 0,
            right: 0,
            padding: "24px",
            background: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
          variants={buttonVariants}
          initial="initial"
          animate="animate"
        >
          <button
            onClick={addCoOwner}
            disabled={coOwners.length >= 3}
            style={{
              width: "100%",
              maxWidth: "460px",
              padding: "12px",
              background: coOwners.length >= 3 ? "#F3F4F6" : "white",
              border: `1px dashed ${
                coOwners.length >= 3 ? "#D1D5DB" : "#7A4FFF"
              }`,
              borderRadius: "8px",
              color: coOwners.length >= 3 ? "#9CA3AF" : "#7A4FFF",
              fontSize: "16px",
              fontWeight: "500",
              cursor: coOwners.length >= 3 ? "not-allowed" : "pointer",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "-5px",
            }}
          >
            <span style={{ fontSize: "20px" }}>+</span> 공동소유자 추가
          </button>

          <button
            className="confirm-button"
            onClick={handleNext}
            disabled={!isNextEnabled || isSending}
            style={{
              width: "100%",
              maxWidth: "460px",
              height: "56px",
              fontSize: "16px",
              fontWeight: "600",
              background: isNextEnabled && !isSending ? "#7A4FFF" : "#EBEBFF",
              color: isNextEnabled && !isSending ? "#EBEBFF" : "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: isNextEnabled && !isSending ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              boxShadow:
                isNextEnabled && !isSending
                  ? "0 2px 5px rgba(0, 117, 255, 0.2)"
                  : "none",
              marginBottom: "30px",
            }}
          >
            {isSending ? "다음" : "다음"}
          </button>
        </motion.div>

        {/* 전송 중 로딩 모달 */}
        {isSending && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                width: "80%",
                maxWidth: "320px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "25px",
                  borderTop: "3px solid #7A4FFF",
                  borderRight: "3px solid transparent",
                  margin: "0 auto 16px",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                전자서명 전송 중
              </h3>
              <p
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "15px",
                  color: "#666",
                }}
              >
                {coOwners.filter((o) => o.isCompleted).length}명의
                공동소유자에게 전자서명을 전송하고 있습니다.
              </p>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "#eee",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${sendingProgress}%`,
                    height: "100%",
                    backgroundColor: "#7A4FFF",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                  }}
                ></div>
              </div>
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "14px",
                  color: "#999",
                }}
              >
                완료될 때까지 화면을 닫지 마세요.
              </p>
            </div>
          </div>
        )}

        {/* 성공 모달 */}
        {showSuccessModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                width: "80%",
                maxWidth: "320px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "#7A4FFF",
                  borderRadius: "30px",
                  margin: "0 auto 16px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                    fill="white"
                  />
                </svg>
              </div>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                전자서명 전송 완료
              </h3>
              <p
                style={{
                  margin: "0",
                  fontSize: "15px",
                  color: "#666",
                }}
              >
                모든 공동소유자에게 전자서명 요청이
                <br />
                성공적으로 전송되었습니다.
              </p>
            </div>
          </div>
        )}

        {/* 확인 모달 */}
        <ConfirmInfoModal
          isOpen={showConfirmModal}
          onClose={handleConfirmModalClose}
          onConfirm={handleConfirmModalConfirm}
          formData={getCoOwnerInfoForModal()}
          isCoOwnerInfo={true}
        />

        <style jsx>{`
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </motion.div>
    </AnimatePresence>
  );
}

export default CoOwnerInfoPage;
